import { SearchBar, type Column } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { machineAccounts, type MachineAccount } from "../../api/machineAccounts.service";
import { DataTable } from "../../components/DataTable/DataTable";
import { PaginationControls } from "../../components/PaginationControls/PaginationControls";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import { formatDateTimeShort } from "../../utils/dateFormatter";
import { ROUTES } from "../../routes";
import styles from "./MachineAccounts.module.css";

type MachineAccountListContext = {
  refetchTrigger?: number;
};

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case "verified":
      return styles.statusVerified;
    case "suspended":
      return styles.statusSuspended;
    default:
      return styles.statusPending;
  }
}

const COLUMNS: Column<MachineAccount>[] = [
  { header: "Name", accessor: "name" },
  { header: "Email", accessor: "email" },
  {
    header: "Status",
    accessor: "status",
    render: (_value: unknown, account: MachineAccount) => (
      <span className={`${styles.statusBadge} ${getStatusBadgeClass(account.status)}`}>
        {account.status}
      </span>
    ),
  },
  {
    header: "Created",
    accessor: "createdAt",
    render: (_value: unknown, account: MachineAccount) => formatDateTimeShort(account.createdAt),
  },
];

function MachineAccountList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOutletContext<MachineAccountListContext>();
  const [displayedAccounts, setDisplayedAccounts] = useState<MachineAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const itemsPerPage = Number(searchParams.get("pageSize")) || 25;

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function updateURL(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      updateURL({ search: value, page: "1" });
    }, 300);
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);
    updateURL({ page: String(page) });
  }

  async function loadAccounts() {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await machineAccounts.getAll({
        search: searchTerm || undefined,
        limit: itemsPerPage,
        offset,
      });
      setDisplayedAccounts(response.accounts);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load machine accounts"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAccounts();
  }, [currentPage, itemsPerPage, searchParams.get("search")]);

  useEffect(() => {
    if (context.refetchTrigger !== undefined && context.refetchTrigger > 0) {
      loadAccounts();
    }
  }, [context.refetchTrigger]);

  function handleRowClick(account: MachineAccount) {
    navigate(ROUTES.machineAccounts.detail(account.id));
  }

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load"
        message={`Failed to load machine accounts: ${error.message}`}
      />
    );
  }

  return (
    <div>
      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search machine accounts..."
        />
      </div>

      <div className={styles.contentSection}>
        {loading ? (
          <LoadingState />
        ) : (
          <>
            <DataTable
              data={displayedAccounts}
              columns={COLUMNS}
              onRowClick={handleRowClick}
              emptyMessage="No machine accounts found."
              rowKey="id"
            />
            <PaginationControls
              currentPage={currentPage}
              totalPages={Math.ceil(total / itemsPerPage)}
              startIndex={(currentPage - 1) * itemsPerPage}
              endIndex={currentPage * itemsPerPage}
              totalCount={total}
              itemName="machine accounts"
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default MachineAccountList;
